import * as THREE from 'three'
import { MathUtils } from 'three'
import { useEffect, useRef, useState } from 'react'
import { Canvas, useFrame } from '@react-three/fiber'
import { OrbitControls } from '@react-three/drei'
import * as Tone from 'tone'
import inst from "./inst"
import anime from 'animejs/lib/anime.es.js';

let size = 0.005
let geos = new Array(19).fill().map((_, i) => new THREE.TorusBufferGeometry((i + 0.5) * size, size * 0.25, 8, 2, Math.PI))
let material = new THREE.MeshBasicMaterial({
  color: 'white'
})

let colorIndex = 0
// let colors = ['#4e6b62', '#772f57', '#005438', '#b9202f']
let colors = ['#000000']
let hit = 0;
let step = 0;
let getInst = () => {
  hit++;
  return (hit % 2 === 0) ? inst.synthL : inst.synthR
}
let lerp = MathUtils.lerp

let getRand = (arr) => arr[Math.floor(Math.random() * arr.length)]
let notes = ["C", "D", "F", "G", "A"]
let octaves = [0, 1, 2, 3, 4, 5, 6, 7, 8]
let allNotes = new Array(notes.length * octaves.length).fill().fill().map((val, i) => {
  return notes[i % notes.length] + octaves[Math.floor(i / notes.length)]
})
let durs = [0.2]
let octaveOffset = 1;
let offsets = [4]

function Sequence({ updated, length, ready, position, size}) {
  const all = useRef()
  const top = useRef()
  const bottom = useRef()
  let time = 0;
  let dur = 0
  let durMult = getRand([1,2,4,1,2,4,8])
  let noteOffset = getRand([-5,-4,-3,-2,-1,0,1,2,3])
  let octave = getRand([4,5,6])
  let data = Array.from({ length }, (v, i) => {
    let note = allNotes[noteOffset+i+(octave*notes.length)];
    let frac = 0.48 - (i / length)
    let size = 0.25
    let pos = {
      x: Math.cos(frac * Math.PI) * size * 1.75,
      y: Math.sin(frac * Math.PI) * size * 1.5,
      z: 0
    }
    dur = getRand(durs) * durMult;
    let velocity = (Math.random() * dur * 2)
    let scale = 1
    let ret = { time, note, i, pos, dur, currPos: { x: 0, y: 0, z: 0 }, velocity, scale, currScale: 1, durMult }
    time += dur
    return ret
  });
  let loopEnd = time
  const part = new Tone.Part(((time, val) => {
    let s = getInst();
    s.triggerAttackRelease(data[val.i].note, 0.01, time, val.velocity);
    data[val.i].scale = 1 + (0.3 / (val.i + 1))
    anime({
      targets: data[val.i],
      scale: 1,
      duration: loopEnd * 400
    })
  }), data);

  useEffect(() => {
    if (ready) {
      part.start();
    }
    return () => part.stop()
  }, [ready, updated])
  part.loop = true;
  part.loopStart = 0;
  part.loopEnd = loopEnd;
  
  useFrame((state) => {
    all.current.position.set(position[0] * mut.zoom2, 0, 0)
    let lerped = lerp(all.current.rotation.x, Math.PI*mut.rotDir, 0.1)*mut.zoom2
    all.current.rotation.set(lerped,0, 0)
    top.current.children.forEach((child, i) => {
      child.scale.set(1,data[i].scale,1)
    })
    bottom.current.children.forEach((child, i) => {
      child.scale.set(1,data[i].scale,1)
    })
  })
  return (
    <group ref={all} position={position}>
      <group ref={top}>
        {data.map((curr, i) => (
          <mesh key={i} geometry={geos[i]} material={material} />
        ))}
      </group>
      <group ref={bottom} rotation={[Math.PI, 0, 0]} position={[size, 0, 0]}>
        {data.map((curr, i) => (
          <mesh key={i} geometry={geos[i]} material={material} />
        ))}
      </group>
    </group>
  )
}

let canSpawn = true;
let mut = { zoom: 1, zoom2: 1, rot: 0, rot2: 0, rot3: 0, reverse: false, rotDir: 1 };
let easing = 'easeInQuart'
let easing2 = 'easeOutQuart'
let easing3 = 'easeInOutQuart'
let duration = 500
let first = false
let len = 20
export default function App() {
  const cam = useRef()

  const [ready, set] = useState(false)
  const [updated, setUpdated] = useState(false)
  const [length, setLength] = useState(len)
  const click = () => {
    Tone.Transport.start();
    Tone.context.resume();

    if (!first) {
      duration = 400
      first = true
    }
    if (canSpawn) {
      anime({
        targets: mut,
        zoom: 0,
        zoom2: 0,
        duration,
        easing,
      })
      setTimeout(() => {
        setLength(len)
        setUpdated(!updated)
        set(true)
        canSpawn = false
        octaveOffset = getRand(offsets)
        mut.rotDir = getRand([1,-1])
        setTimeout(() => {
          anime({
            targets: mut,
            zoom: 1,
            zoom2: 1,
            duration,
            easing: easing2,
          })
        }, 50)
        let dTime = 0.2 + Math.floor(0.2 * (Math.random() * 3))
        let dTime2 = 0.2 + Math.floor(0.2 * (Math.random() * 3))
        let rTime = 0.5 + Math.random() * 1
        inst.delayL.delayTime.linearRampToValueAtTime(dTime, rTime)
        inst.delayR.delayTime.linearRampToValueAtTime(dTime2, rTime)

      }, duration + 50)
      let val = 2000;
      let time = (duration * 1.5) / 1000;
      inst.filterL.frequency.rampTo(val, time * 0.5);
      inst.filterR.frequency.rampTo(val, time * 0.5)
      setTimeout(() => {
        inst.filterL.frequency.rampTo(inst.defaultFreq, time);
        inst.filterR.frequency.rampTo(inst.defaultFreq, time)

      }, time * 1000)
      anime({
        targets: mut,
        rot: mut.rot + getRand([1]) * getRand([1, -1]),
        duration: duration * 2,
        easing: easing3,
        complete: function (anim) {
          canSpawn = true
        }
      })

    }
  }
  let isMobile = window.innerWidth < 800
  let spirals = new Array(6).fill().map(() => getRand([1, 2,3,3, 4, 5, 7]))
  spirals.splice(Math.floor(Math.random() * spirals.length), 0, 7)
  let distances = spirals.map((spiral, i) => {
    let dist = 0;
    spirals.forEach((val, j) => i >= j ? dist += val : null)
    return dist
  })
  let halfX = -distances[distances.length - 1] * size
  return (
    <div className="fullscreen" onMouseDown={() => { click() }}>
      <Canvas dpr={[1, 2]} camera={{ position: [0, 0, isMobile ? 0.2 : 0.2], fov: 75, near: 0.001 }}>
        <OrbitControls enableRotate={false} ref={cam} />
        <color attach="background" args={['#000000']} />
        <ambientLight intensity={0.75} />
        <pointLight position={[-30, 10, 5]} intensity={6} />
        <pointLight position={[30, 10, -5]} intensity={3} />
        <group rotation={[0, 0, Math.PI * 0.75]}>
          {spirals.map((curr, i) => (
            <Sequence position={[halfX + (spirals[i] * size + ((distances[i] - spirals[i]) * size * 2)), 0, 0]} updated={updated} key={i} index={1} ready={ready} length={curr} size={size} />
          ))}
        </group>
      </Canvas>
    </div>
  )
}

console.log('%c🌱🌱 @ramioso 2021-10-31 🌱🌱 ', 'color: orange');